/**
 * Sayz Bedenimi Bul — Ziyaretçi Profili (localStorage)
 *
 * Ziyaretçi wizard'ı ilk kez tamamladığında topladığımız cevaplar burada
 * saklanır. Sonraki ürünlerde wizard aynı ziyaretçiden zaten bilinen bilgileri
 * sormaz → "Merhaba tekrar, 2 sorum daha var" akışı bu hafızayla mümkün olur.
 *
 * Sayz DB'de kişisel veri tutmayı MVP'ye almadık: profil sadece cihazda kalır.
 * Merchant ayrı bir cihazdan bakınca profil boş görünür. İlerideki iterasyonda
 * opsiyonel bir "hesabıma bağla" özelliği ile server-side persistence eklenebilir.
 *
 * Preview sayfası, wizard preview'ini render ederken bu profili okur; merchant
 * "önizlemede birikimli deneyimi" test edebilir. "Profili Sıfırla" butonu ile
 * geri alınabilir.
 */

const PROFILE_KEY = "sayz.fms.profile.v1";

/**
 * Şema (tüm alanlar opsiyonel; sadece kullanıcının yanıtladıklarını tutuyoruz):
 * {
 *   height: number,      // cm
 *   weight: number,      // kg
 *   age: number,         // years
 *   fit: "tight"|"regular"|"loose",
 *   chest: "narrow"|"normal"|"wide",
 *   waist: "flat"|"normal"|"round",
 *   hip: "narrow"|"normal"|"wide",
 *   shoulder: "narrow"|"normal"|"wide",
 *   updatedAt: ISO string
 * }
 */

export const loadFmsProfile = () => {
    try {
        const raw = localStorage.getItem(PROFILE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return typeof parsed === "object" && parsed ? parsed : {};
    } catch {
        return {};
    }
};

export const saveFmsProfile = (patch) => {
    try {
        const current = loadFmsProfile();
        const next = { ...current, ...patch, updatedAt: new Date().toISOString() };
        localStorage.setItem(PROFILE_KEY, JSON.stringify(next));
        return next;
    } catch {
        return null;
    }
};

export const resetFmsProfile = () => {
    try {
        localStorage.removeItem(PROFILE_KEY);
    } catch { /* ignore */ }
};

/** Profil zaten dolu mu — "Merhaba tekrar" başlığı için sinyal. */
export const hasAnyProfileData = (p = loadFmsProfile()) => {
    return Boolean(p && (p.height || p.weight || p.age || p.fit));
};

/**
 * Bir ürünün istediği alanlar (ör. ["chest", "waist"]) ile profilde doldurulmuş
 * alanları karşılaştırıp {complete, missing, filled} döndürür. "basic" (boy +
 * kilo + yaş) ve "fit" her zaman zorunlu kabul edilir çünkü öneri motoru bu
 * dört değeri kullanır — vücut alanları ürün ölçü grubuna göre değişir.
 */
const BASIC_FIELDS = ["height", "weight", "age", "fit"];

export const analyzeProfile = (profile = {}, requiredBodyFields = []) => {
    const p = profile || {};
    const allRequired = [...BASIC_FIELDS, ...requiredBodyFields];
    const filled = allRequired.filter((f) => p[f] !== undefined && p[f] !== null && p[f] !== "");
    const missing = allRequired.filter((f) => !filled.includes(f));
    const hasAnyData = filled.length > 0;
    const missingBasic = BASIC_FIELDS.filter((f) => !filled.includes(f));
    const missingBody = requiredBodyFields.filter((f) => !filled.includes(f));
    return {
        hasAnyData,
        complete: missing.length === 0,
        filled,
        missing,
        missingBasic,
        missingBody,
    };
};

/**
 * Preview'de merchant'ın 3 "Smart Recall" senaryosunu test edebilmesi için
 * yapay profiller. Live'da localStorage'daki gerçek ziyaretçi profili
 * kullanılır — bu stub'lar sadece merchant panel önizlemesindedir.
 */
export const PREVIEW_PROFILE_STUBS = {
    empty: {},
    partial: {
        height: 172,
        weight: 68,
        age: 28,
        fit: "regular",
        chest: "normal",
        shoulder: "wide",
        // waist + hip eksik — bu ürün için eksik varsayılan
    },
    complete: {
        height: 172,
        weight: 68,
        age: 28,
        fit: "regular",
        chest: "normal",
        waist: "normal",
        hip: "normal",
        shoulder: "wide",
    },
};
